import { createApi } from '@reduxjs/toolkit/query/react';

import { baseQueryWithErrorHandling } from './baseQuery';

export interface BrowserUseScraperTab {
  url: string;
  title: string;
  tab_id: string | null;
  parent_tab_id: string | null;
}

export interface BrowserUseScraperInteractedElement {
  tagName: string | null;
  xpath: string | null;
  ariaName: string | null;
  attributes: Record<string, string> | null;
}

export interface BrowserUseScraperStep {
  id: string;
  step_number: number;
  status: 'started' | 'completed';
  url: string | null;
  title: string | null;
  thinking: string | null;
  evaluation_previous_goal: string | null;
  memory: string | null;
  next_goal: string | null;
  current_plan_item: number | null;
  plan_update: string[] | null;
  actions: Record<string, unknown>[];
  results: Record<string, unknown>[];
  extracted_content: string | null;
  is_done: boolean;
  tabs: BrowserUseScraperTab[];
  interacted_elements: (BrowserUseScraperInteractedElement | null)[];
  screenshot: string | null;
  screenshot_url: string | null;
  duration_seconds: number | null;
  timestamp: string | null;
  created_at: string;
}

export interface BrowserUseScraperRunListItem {
  id: string;
  workflow_id: string;
  status: 'pending' | 'running' | 'completed' | 'failed';
  login_kind: string | null;
  base_url: string | null;
  project_id: string | null;
  max_steps: number;
  successful: boolean | null;
  total_steps: number | null;
  error_message: string | null;
  gif_s3_key: string | null;
  started_at: string | null;
  completed_at: string | null;
  created_at: string;
  step_count: number;
}

export interface BrowserUseScraperRunDetail {
  id: string;
  workflow_id: string;
  status: 'pending' | 'running' | 'completed' | 'failed';
  login_kind: string | null;
  base_url: string | null;
  project_id: string | null;
  task: string;
  schema_json: string;
  max_steps: number;
  blob_storage_bucket: string;
  successful: boolean | null;
  total_steps: number | null;
  structured_output: Record<string, unknown> | null;
  error_message: string | null;
  gif_s3_key: string | null;
  usage: {
    totalCost: number;
    totalTokens: number;
    totalPromptTokens: number;
    totalCompletionTokens: number;
  } | null;
  started_at: string | null;
  completed_at: string | null;
  created_at: string;
  modified_at: string;
  steps: BrowserUseScraperStep[];
}

interface RunListResponse {
  count: number;
  results: BrowserUseScraperRunListItem[];
}

export const browserUseScraperApiClient = createApi({
  reducerPath: 'browserUseScraperApi',
  baseQuery: baseQueryWithErrorHandling,
  tagTypes: ['BrowserUseScraperRun', 'BrowserUseScraperSteps'],
  endpoints: (builder) => ({
    getScraperRuns: builder.query<RunListResponse, { status?: string; limit?: number; offset?: number }>({
      query: (params) => {
        const searchParams = new URLSearchParams();
        if (params.status) searchParams.set('status', params.status);
        if (params.limit) searchParams.set('limit', String(params.limit));
        if (params.offset) searchParams.set('offset', String(params.offset));
        return `api/browser-use-scraper/runs?${searchParams.toString()}`;
      },
      providesTags: ['BrowserUseScraperRun'],
    }),

    getScraperRunDetail: builder.query<BrowserUseScraperRunDetail, string>({
      query: (id) => `api/browser-use-scraper/runs/${id}`,
      providesTags: (result) =>
        result ? [{ type: 'BrowserUseScraperRun', id: result.id }] : [],
    }),

    getScraperRunByWorkflowId: builder.query<BrowserUseScraperRunDetail, string>({
      query: (workflowId) =>
        `api/browser-use-scraper/runs/by-workflow-id?workflow_id=${encodeURIComponent(workflowId)}`,
      providesTags: (result) =>
        result ? [{ type: 'BrowserUseScraperRun', id: result.id }] : [],
    }),

    getScraperRunSteps: builder.query<BrowserUseScraperStep[], { runId: string; sinceStep?: number }>({
      query: ({ runId, sinceStep }) => {
        const params = sinceStep != null ? `?since_step=${sinceStep}` : '';
        return `api/browser-use-scraper/runs/${runId}/steps${params}`;
      },
      providesTags: (result, error, { runId }) => [
        { type: 'BrowserUseScraperSteps', id: runId },
      ],
    }),
  }),
});

export const {
  useGetScraperRunsQuery,
  useGetScraperRunDetailQuery,
  useGetScraperRunByWorkflowIdQuery,
  useGetScraperRunStepsQuery,
} = browserUseScraperApiClient;
